import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';

export type DocumentLanguage = 'auto' | 'de' | 'en';

export interface PrivacyAccess {
  allowed: boolean;
  maxUploadBytes: number;
  formats: string[];
}

export interface AnonymizationResult {
  file: Blob;
  fileName: string;
  language: string;
  /** Replaced entries per entity type, e.g. `{ PERSON: 2 }`. */
  counts: Record<string, number>;
  /** Hints from the privacy-api, e.g. `images_not_checked`, `comments_removed`. */
  notices: string[];
}

/** Error with a message from the backend that can be shown to the user as is. */
export class AnonymizationError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'AnonymizationError';
  }
}

export interface PrivacyApi {
  getAccess(): Promise<PrivacyAccess>;
  anonymize(
    file: File,
    language: DocumentLanguage,
  ): Promise<AnonymizationResult>;
}

export const privacyApiRef = createApiRef<PrivacyApi>({
  id: 'plugin.visionflow.privacy',
});

/** Reads the file name from `filename*=UTF-8''…` (RFC 5987), falling back to `filename="…"`. */
export function parseFileName(
  disposition: string | null,
  fallback: string,
): string {
  if (!disposition) return fallback;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Malformed encoding: use the plain variant below.
    }
  }
  const plain = /filename="([^"]+)"/i.exec(disposition);
  return plain ? plain[1] : fallback;
}

export class PrivacyClient implements PrivacyApi {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  private async baseUrl() {
    return `${await this.deps.discoveryApi.getBaseUrl('visionflow')}/privacy`;
  }

  private async fail(response: Response): Promise<never> {
    const body = await response.json().catch(() => undefined);
    throw new AnonymizationError(
      body?.error?.message ?? response.statusText,
      response.status,
    );
  }

  async getAccess(): Promise<PrivacyAccess> {
    const response = await this.deps.fetchApi.fetch(
      `${await this.baseUrl()}/access`,
    );
    if (!response.ok) return this.fail(response);
    return response.json();
  }

  async anonymize(
    file: File,
    language: DocumentLanguage,
  ): Promise<AnonymizationResult> {
    const body = new FormData();
    body.append('file', file, file.name);
    body.append('language', language);

    const response = await this.deps.fetchApi.fetch(
      `${await this.baseUrl()}/anonymize`,
      {
        method: 'POST',
        body,
      },
    );
    if (!response.ok) return this.fail(response);

    const summary = JSON.parse(
      response.headers.get('x-anonymization-summary') ?? '{}',
    );
    return {
      file: await response.blob(),
      fileName: parseFileName(
        response.headers.get('content-disposition'),
        file.name,
      ),
      language: summary.language ?? language,
      counts: summary.counts ?? {},
      notices: summary.notices ?? [],
    };
  }
}
